import { calculateSubnetMindshare } from '../utils/mindshareCalculator';
import { saveTensorpulseMindshare, shouldUpdateTensorpulseMindshare, updateLastUpdateTimestamp } from './tensorpulseMindshareService';
import { updateSubnetOverview } from './subnetPoolService';
import { updateAllCurrentMindshare } from './currentMindshareService';
import axios from 'axios';
import { rateLimiter } from './rateLimiter';
import { clearAllCaches } from '../utils/cacheUtils';

class AutoUpdateService {
  private static instance: AutoUpdateService | null = null;
  private updateInterval: NodeJS.Timeout | null = null;
  private readonly UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes (increased from 30 minutes)
  private isUpdating = false;
  private lastUpdateTime: Date | null = null;
  private autoUpdateEnabled = false;
  private lastHistoryUpdate: Date | null = null; // Initially null
  private readonly HISTORY_UPDATE_INTERVAL_MS = 120 * 60 * 1000; // 120 minutes (increased from 60 minutes)
  private readonly MINDSHARE_UPDATE_INTERVAL_MS = 120 * 60 * 1000; // 120 minutes (increased from 60 minutes)
  private lastMindshareUpdate: Date | null = null;
  private lastCurrentMindshareUpdate: Date | null = null;
  private readonly CURRENT_MINDSHARE_UPDATE_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes (increased from 10 minutes)

  private constructor() {
    // Private constructor to enforce singleton pattern
    // Try to load last update time from localStorage if available
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedTime = window.localStorage.getItem('lastHistoryUpdate');
      if (savedTime) {
        try {
          this.lastHistoryUpdate = new Date(savedTime);
          console.log(`Loaded last history update time from storage: ${this.lastHistoryUpdate.toLocaleString()}`);
        } catch (e) {
          console.error('Failed to parse saved update time:', e);
        }
      }
      
      const savedMindshareTime = window.localStorage.getItem('lastMindshareUpdate');
      if (savedMindshareTime) {
        try {
          this.lastMindshareUpdate = new Date(savedMindshareTime);
          console.log(`Loaded last mindshare update time from storage: ${this.lastMindshareUpdate.toLocaleString()}`);
        } catch (e) {
          console.error('Failed to parse saved mindshare update time:', e);
        }
      }
      
      const savedCurrentMindshareTime = window.localStorage.getItem('lastCurrentMindshareUpdate');
      if (savedCurrentMindshareTime) {
        try {
          this.lastCurrentMindshareUpdate = new Date(savedCurrentMindshareTime);
          console.log(`Loaded last current mindshare update time from storage: ${this.lastCurrentMindshareUpdate.toLocaleString()}`);
        } catch (e) {
          console.error('Failed to parse saved current mindshare update time:', e);
        }
      }
    }
  }

  public static getInstance(): AutoUpdateService {
    if (!AutoUpdateService.instance) {
      AutoUpdateService.instance = new AutoUpdateService();
    }
    return AutoUpdateService.instance;
  }

  public startAutoUpdate(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    // Enable auto-update
    this.autoUpdateEnabled = true;
    
    // Perform initial update after a delay to avoid initial load performance issues
    setTimeout(() => {
      this.performUpdate();
      
      // Set up interval for future updates
      this.updateInterval = setInterval(() => {
        this.performUpdate();
      }, this.UPDATE_INTERVAL_MS);
    }, 30000); // 30 second delay for initial update
    
    console.log('Auto-update service started with 60-minute interval');
  }

  public enableAutoUpdate(): void {
    if (!this.autoUpdateEnabled) {
      this.startAutoUpdate();
    }
  }

  public stopAutoUpdate(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
      this.autoUpdateEnabled = false;
      console.log('Auto-update service stopped');
    }
  }

  private shouldUpdateHistory(): boolean {
    if (!this.lastHistoryUpdate) {
      console.log('No previous history update found, update needed');
      return true;
    }
    
    const timeSinceLastUpdate = Date.now() - this.lastHistoryUpdate.getTime();
    const shouldUpdate = timeSinceLastUpdate >= this.HISTORY_UPDATE_INTERVAL_MS;
    
    if (shouldUpdate) {
      console.log(`Last history update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago, update needed`);
    } else {
      console.log(`Last history update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago, update not needed yet`);
    }
    
    return shouldUpdate;
  }
  
  private shouldUpdateMindshare(): boolean {
    if (!this.lastMindshareUpdate) {
      console.log('No previous mindshare update found, update needed');
      return true;
    }
    
    const timeSinceLastUpdate = Date.now() - this.lastMindshareUpdate.getTime();
    const shouldUpdate = timeSinceLastUpdate >= this.MINDSHARE_UPDATE_INTERVAL_MS;
    
    if (shouldUpdate) {
      console.log(`Last mindshare update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago, update needed`);
    } else {
      console.log(`Last mindshare update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago, update not needed yet`);
    }
    
    return shouldUpdate;
  }
  
  private shouldUpdateCurrentMindshare(): boolean {
    if (!this.lastCurrentMindshareUpdate) {
      console.log('No previous current mindshare update found, update needed');
      return true;
    }
    
    const timeSinceLastUpdate = Date.now() - this.lastCurrentMindshareUpdate.getTime();
    const shouldUpdate = timeSinceLastUpdate >= this.CURRENT_MINDSHARE_UPDATE_INTERVAL_MS;
    
    if (shouldUpdate) {
      console.log(`Last current mindshare update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago, update needed`);
    } else {
      console.log(`Last current mindshare update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago, update not needed yet`);
    }
    
    return shouldUpdate;
  }

  private async performUpdate(): Promise<void> {
    if (!this.autoUpdateEnabled || this.isUpdating) return;
    
    try {
      this.isUpdating = true;
      console.log(`Starting data update at ${new Date().toLocaleString()}`);

      // Clear all caches before updating
      clearAllCaches();

      // Fetch subnet data from API - with reduced frequency
      try {
        await rateLimiter.checkRateLimit();
        const response = await axios.get(
          'https://api.taostats.io/api/dtao/pool/latest/v1?page=1&limit=100&order=netuid_asc',
          {
            headers: {
              'accept': 'application/json',
              'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
            },
            timeout: 15000 // 15 second timeout
          }
        );

        if (!response.data || !Array.isArray(response.data.data)) {
          throw new Error('Invalid response format');
        }

        // Update only a subset of subnets per cycle to reduce load
        const subnets = response.data.data
          .filter((subnet: any) => subnet.netuid !== 0)
          .slice(0, 20); // Only process 20 subnets per update cycle

        // Update each subnet's data
        const updatePromises = subnets.map((subnet: any) => updateSubnetOverview(subnet.netuid, subnet));
        await Promise.all(updatePromises);
      } catch (error) {
        console.error('Error fetching subnet data:', error);
        // Continue with other updates even if this one fails
      }
      
      // Check if we should update TensorPulse mindshare - with reduced frequency
      const shouldUpdateTensorPulse = await shouldUpdateTensorpulseMindshare();
      if (shouldUpdateTensorPulse && this.shouldUpdateMindshare()) {
        console.log('Updating TensorPulse mindshare data');
        
        try {
          // Clear all cache before calculating mindshare
          clearAllCaches();
          
          // Calculate mindshare data
          const mindshareData = await calculateSubnetMindshare();
          if (mindshareData.length > 0) {
            const shouldUpdateHistoryToo = this.shouldUpdateHistory();
            console.log(`History update ${shouldUpdateHistoryToo ? 'needed' : 'skipped'} - Last update was ${this.lastHistoryUpdate?.toLocaleString() || 'never'}`);
            
            // Save to TensorPulse mindshare collection - only update a subset
            const topSubnets = mindshareData.slice(0, 20); // Only update top 20 subnets
            const tensorPulseSavePromises = topSubnets.map(data => 
              saveTensorpulseMindshare({
                ...data,
                _forceHistoryUpdate: shouldUpdateHistoryToo
              })
            );
            
            await Promise.all(tensorPulseSavePromises);
            
            // Update the last update timestamp
            await updateLastUpdateTimestamp();
            console.log('TensorPulse mindshare update completed');
            
            // Update mindshare timestamp
            this.lastMindshareUpdate = new Date();
            console.log(`Updated mindshare timestamp to ${this.lastMindshareUpdate.toLocaleString()}`);
            
            // Save to localStorage if available
            if (typeof window !== 'undefined' && window.localStorage) {
              window.localStorage.setItem('lastMindshareUpdate', this.lastMindshareUpdate.toISOString());
            }
            
            if (shouldUpdateHistoryToo) {
              this.lastHistoryUpdate = new Date();
              console.log(`Updated history timestamp to ${this.lastHistoryUpdate.toLocaleString()}`);
              
              // Save to localStorage if available
              if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.setItem('lastHistoryUpdate', this.lastHistoryUpdate.toISOString());
              }
            }
          }
        } catch (error) {
          console.error('Error updating TensorPulse mindshare:', error);
        }
      } else {
        console.log('Skipping TensorPulse mindshare update - not time yet');
      }
      
      // Check if we should update current mindshare collection
      const shouldUpdateCurrentMindshareData = this.shouldUpdateCurrentMindshare();
      console.log(`Current mindshare update ${shouldUpdateCurrentMindshareData ? 'needed' : 'skipped'} - Last update was ${this.lastCurrentMindshareUpdate?.toLocaleString() || 'never'}`);
      
      if (shouldUpdateCurrentMindshareData) {
        try {
          // Update current mindshare collection
          const updatedCount = await updateAllCurrentMindshare();
          console.log(`Updated ${updatedCount} entries in current_mindshare collection`);
          
          // Update timestamp
          this.lastCurrentMindshareUpdate = new Date();
          console.log(`Updated current mindshare timestamp to ${this.lastCurrentMindshareUpdate.toLocaleString()}`);
          
          // Save to localStorage if available
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('lastCurrentMindshareUpdate', this.lastCurrentMindshareUpdate.toISOString());
          }
        } catch (error) {
          console.error('Error updating current mindshare:', error);
        }
      }
      
      this.lastUpdateTime = new Date();
      console.log(`Data update completed at ${this.lastUpdateTime.toLocaleString()}`);
    } catch (error) {
      console.error('Error during automatic update:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  public getLastUpdateTime(): Date | null {
    return this.lastUpdateTime;
  }

  public getLastHistoryUpdateTime(): Date | null {
    return this.lastHistoryUpdate;
  }
  
  public getLastMindshareUpdateTime(): Date | null {
    return this.lastMindshareUpdate;
  }
  
  public getLastCurrentMindshareUpdateTime(): Date | null {
    return this.lastCurrentMindshareUpdate;
  }

  public isRunning(): boolean {
    return this.autoUpdateEnabled;
  }

  public async forceUpdate(): Promise<void> {
    // Force a history update on manual refresh
    this.lastHistoryUpdate = null;
    this.lastMindshareUpdate = null;
    this.lastCurrentMindshareUpdate = null;
    
    // Clear localStorage if available
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('lastHistoryUpdate');
      window.localStorage.removeItem('lastMindshareUpdate');
      window.localStorage.removeItem('lastCurrentMindshareUpdate');
      
      // Clear all cache-related items
      clearAllCaches();
    }
    
    console.log('Forcing update with history and mindshare refresh');
    return this.performUpdate();
  }
}

export const autoUpdateService = AutoUpdateService.getInstance();